import React, { useMemo, useRef } from 'react';
import { DotsVerticalIcon } from '@heroicons/react/outline';
import { CheckCircleIcon, PauseIcon, PlayIcon, ExclamationCircleIcon } from '@heroicons/react/solid';
import clsx from 'clsx';
import DropdownMenu from './DropdownMenu';
import { useTabNavigation } from '../../../routes/Home';

const CampaignHeader = ({
	title,
	description,
	icon,
	status,
	statusConfig,
	hasSequences,
	isEditing,
	isUpdating,
	isDropdownOpen,
	setIsDropdownOpen,
	onStartEditing,
	onToggleStatus,
	onCancel,
	onSave,
	canSave
}) => {
	const dropdownButtonRef = useRef(null);
	
	// Add error handling for the context
	let navigateToCampaignInsights;
	try {
		const tabNavigation = useTabNavigation();
		navigateToCampaignInsights = tabNavigation.navigateToCampaignInsights;
		console.log('Tab navigation context loaded successfully');
	} catch (error) {
		console.error('Failed to load tab navigation context:', error);
		// Fallback function
		navigateToCampaignInsights = () => {
			console.log('Fallback: Opening insights for campaign');
		};
	}

	const handleInsightsClick = () => {
		console.log('Insights button clicked - using slug-based navigation');
		try {
			navigateToCampaignInsights();
			setIsDropdownOpen(false);
			console.log('Successfully called navigateToCampaignInsights');
		} catch (error) {
			console.error('Error navigating to insights:', error);
		}
	};

	return (
		<div className={clsx(
			'flex flex-row gap-4 p-4 relative rounded-t-lg',
			statusConfig.bgColor
		)}>
			{isUpdating && (
				<div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center">
					<div className="text-sm text-gray-600">Updating...</div>
				</div>
			)}
			<div className="p-3 bg-blue-100 rounded-full mt-1 self-start">
				{icon}
			</div>
			<div className="space-y-2 flex-1">
				<h2 className="text-lg font-medium text-gray-900">{title}</h2>
				<div
					className={clsx(
						"inline-flex items-center gap-x-1.5 rounded-full px-2 py-1 transition-all duration-200",
						isUpdating && "animate-pulse"
					)}
					style={{ backgroundColor: `${statusConfig.color}1A` }}
				>
					<span
						className="h-1.5 w-1.5 rounded-full transition-colors duration-200"
						style={{ backgroundColor: statusConfig.color }}
					/>
					<span
						className="text-xs font-medium transition-colors duration-200"
						style={{ color: statusConfig.color }}
					>
						{statusConfig.label}
					</span>
				</div>
				<p className="text-sm text-gray-600">{description}</p>
			</div>
			<div className="flex flex-col items-end justify-between gap-2 relative z-50">
				{isEditing ? (
					<div className="flex gap-2">
						<button
							onClick={onCancel}
							disabled={isUpdating}
							className="text-sm px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
						>
							Cancel
						</button>
						<div className="relative group">
							<button
								onClick={onSave}
								disabled={isUpdating || !canSave}
								className={clsx(
									"text-sm px-3 py-1.5 rounded text-white disabled:cursor-not-allowed transition-colors",
									canSave 
										? "bg-blue-600 hover:bg-blue-700" 
										: "bg-gray-400 cursor-not-allowed"
								)}
							>
								{isUpdating ? 'Saving...' : 'Save'}
							</button>
							{!canSave && !isUpdating && (
								<div className="absolute bottom-full right-0 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50">
									<div className="flex items-center gap-1">
										<ExclamationCircleIcon className="w-3 h-3" />
										Please fill in all required fields
									</div>
									<div className="absolute top-full right-2 w-0 h-0 border-l-2 border-r-2 border-t-2 border-transparent border-t-gray-800"></div>
								</div>
							)}
						</div>
					</div>
				) : (
					<>
						<div className="relative inline-block text-left">
							<button
								ref={dropdownButtonRef}
								onClick={() => setIsDropdownOpen(!isDropdownOpen)}
								className="p-1 rounded-full hover:bg-gray-100 focus:outline-none"
							>
								<DotsVerticalIcon className="w-5 h-5 text-gray-600" />
							</button>
							
							<DropdownMenu
								isOpen={isDropdownOpen}
								onClose={() => setIsDropdownOpen(false)}
								buttonRef={dropdownButtonRef}
							>
								<button
									onClick={() => {
										onToggleStatus();
										setIsDropdownOpen(false);
									}}
									disabled={isUpdating}
									className="w-full text-left px-4 py-2 text-sm disabled:opacity-50 flex items-center gap-2 hover:bg-gray-100 text-gray-700"
								>
									{status === 'ACTIVE' ? (
										<>
											<PauseIcon className="w-4 h-4" />
											Pause Campaign
										</>
									) : (
										<>
											<PlayIcon className="w-4 h-4" />
											Resume Campaign
										</>
									)}
								</button>
								
								<button
									onClick={() => {
										onStartEditing();
										setIsDropdownOpen(false);
									}}
									className="w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-gray-100 text-gray-700"
								>
									<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
									</svg>
									Edit Sequences
								</button>
								
								<button
									onClick={handleInsightsClick}
									className="w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-gray-100 text-gray-700"
								>
									<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
									</svg>
									Insights
								</button>
							</DropdownMenu>
						</div>
						
						{status === 'ACTIVE' ? (
							<button 
								onClick={onToggleStatus}
								disabled={isUpdating}
								className={clsx(
									"text-green-600 hover:text-green-700 disabled:opacity-50 transition-colors",
									isUpdating && "animate-pulse"
								)}
								title="Pause campaign"
							>
								<PauseIcon className="w-6 h-6" />
							</button>
						) : hasSequences ? (
							<button 
								onClick={onToggleStatus}
								disabled={isUpdating}
								className={clsx(
									"text-blue-600 hover:text-blue-700 disabled:opacity-50 transition-colors",
									isUpdating && "animate-pulse"
								)}
								title="Activate campaign"
							>
								<PlayIcon className="w-6 h-6" />
							</button>
						) : (
							<button
								onClick={onStartEditing}
								disabled={isUpdating}
								className="text-sm px-4 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
							>
								Setup
							</button>
						)}
					</>
				)}
			</div>
		</div>
	);
};

export default CampaignHeader; 